import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { AccountRepairBanner } from '@/_shared/components/AccountRepair/AccountRepairBanner';
import { CopyButton } from '@/_shared/components/CopyButton';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/_shared/components/ui/sheet';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useRunOnceWhen } from '@/_shared/hooks/useRunOnceWhen';
import { ANALYTICS_EVENTS, trackClientEvent } from '@/_shared/lib/analytics';
import { isOrpcNotFoundError } from '@/_shared/lib/orpc-errors';
import { cn } from '@/_shared/lib/utils';
import {
  canRestoreDashboardDraft,
  type ChatDashboardArtifactsStore,
  isArtifactPersisted,
} from '@/chat/hooks/useChatDashboardArtifacts';
import { DashboardParamBar } from '@/dashboard/components/DashboardParamBar';
import { PanelWithData } from '@/dashboard/components/PanelWithData';
import { useDynamicParamOptions } from '@/dashboard/hooks/useDynamicParamOptions';
import { computeDefaults } from '@/dashboard/lib/dashboard-params';
import { toRpcSpec } from '@/dashboard/lib/to-rpc-spec';
import type { DashboardSpec } from '@/dashboard/types';

const previewStatusBadgeClass = 'text-[0.625rem]';

/**
 * One `dashboardPreviewed` per opened preview, not one per panel — a multi-panel dashboard would
 * otherwise report a view per chart. Per-panel query outcomes ride `dashboardQueried`.
 *
 * A child component rather than an effect in the parent: the parent returns early for a removed or
 * missing artifact, and a hook cannot sit below an early return. The caller keys it by artifact, so
 * an agent that builds a second dashboard into the same open panel remounts it — a new clock and a
 * new event, rather than one event for the first artifact only.
 *
 * `success: false` is reachable only for a SAVED dashboard, whose spec loads over the network. A chat
 * draft holds its spec in memory (`artifact.spec` is required), so a draft preview cannot fail and always
 * reports success. A removed or not-found dashboard reports `success: false`. A failing chart rides
 * `dashboardQueried` on either path.
 */
function TrackDashboardPreview({
  settled,
  success,
  panelCount,
  dashboardId,
}: {
  /** The preview has finished resolving. Firing before this reports an empty spec as a failure. */
  settled: boolean;
  success: boolean;
  panelCount: number;
  dashboardId: string | null;
}): null {
  const openedAt = useRef<number | null>(null);
  openedAt.current ??= Date.now();

  useRunOnceWhen(settled, () => {
    trackClientEvent(ANALYTICS_EVENTS.dashboardPreviewed, {
      ...(dashboardId ? { dashboardId } : {}),
      panelCount,
      success,
      durationMs: Date.now() - (openedAt.current ?? Date.now()),
    });
  });

  return null;
}

interface DashboardPreviewProps {
  store: ChatDashboardArtifactsStore;
  chatSessionId?: string;
  onAskAssistant?: (message: string) => void;
  /**
   * Read-only chat (you don't own it): drop every write control — Save, Restore draft, Ask assistant.
   * `dashboard.save` is workspace-scoped rather than chat-owner-scoped, so these would otherwise
   * succeed against someone else's chat instead of being rejected like every other chat write.
   *
   * Required, not optional: an omitted authorization flag must not silently mean "allowed", and a new
   * caller that forgets it should fail to compile rather than quietly restoring the write controls.
   */
  readOnly: boolean;
  className?: string;
}

function DashboardPreviewNotFound({
  title,
  onClose,
  onRestoreDraft,
  className,
}: {
  title: string;
  onClose: () => void;
  onRestoreDraft?: () => void;
  className?: string;
}): React.ReactElement {
  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
          <Badge variant="outline" className={previewStatusBadgeClass}>
            Not found
          </Badge>
        </div>
        <Button variant="tertiary" aria-label="Close preview" onClick={onClose} Icon={<X />} />
      </div>
      <Empty className="flex-1 border-0">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CircleAlert aria-hidden />
          </EmptyMedia>
          <EmptyTitle>Dashboard not found</EmptyTitle>
          <EmptyDescription>
            This dashboard is no longer in the workspace. Chat history is still here, but the live preview is not
            available.
          </EmptyDescription>
        </EmptyHeader>
        {onRestoreDraft ? (
          <EmptyContent>
            <Button variant="secondary" label="Restore draft" onClick={onRestoreDraft} />
          </EmptyContent>
        ) : null}
      </Empty>
    </div>
  );
}

export function DashboardPreview({
  store,
  chatSessionId,
  onAskAssistant,
  readOnly,
  className,
}: DashboardPreviewProps): React.ReactElement | null {
  const { wp } = useWorkspace();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const artifact = store.activeArtifact;
  const [paramValues, setParamValues] = useState<Record<string, string | string[]>>({});

  const isPreSaveDraft = !artifact?.saved;
  const isAgentEditing = artifact?.saved !== null && artifact !== null && !isArtifactPersisted(artifact);
  const useDbSource =
    artifact?.saved !== null && artifact !== null && isArtifactPersisted(artifact) && !artifact.externallyRemoved;

  const savedSlug = artifact?.saved?.slug ?? artifact?.slug ?? '';

  const dashboardQuery = useQuery({
    ...orpc.dashboard.get.queryOptions({
      input: { slug: savedSlug },
    }),
    enabled: useDbSource && savedSlug.length > 0,
    refetchOnMount: 'always',
  });

  const markedRemovedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!artifact) {
      markedRemovedRef.current = null;
      return;
    }
    if (markedRemovedRef.current !== null && markedRemovedRef.current !== artifact.slug) {
      markedRemovedRef.current = null;
    }
    if (!useDbSource || !dashboardQuery.isError) {
      if (dashboardQuery.isSuccess) markedRemovedRef.current = null;
      return;
    }
    if (!isOrpcNotFoundError(dashboardQuery.error)) return;
    if (markedRemovedRef.current === artifact.slug) return;
    markedRemovedRef.current = artifact.slug;
    store.markSavedRemoved(artifact.slug);
  }, [artifact, useDbSource, dashboardQuery.isError, dashboardQuery.isSuccess, dashboardQuery.error, store]);

  const dbDashboard = dashboardQuery.data?.dashboard ?? null;

  useEffect(() => {
    if (!artifact || !dbDashboard || !useDbSource) return;
    if (artifact.title !== dbDashboard.title || (artifact.description ?? null) !== (dbDashboard.description ?? null)) {
      store.syncMetaFromDb(artifact.slug, {
        title: dbDashboard.title,
        description: dbDashboard.description ?? null,
      });
    }
  }, [artifact, dbDashboard, store, useDbSource]);

  // After agent update / auto-save while the panel stays open, seed get-cache from the
  // store then invalidate — otherwise React Query keeps the pre-update row until remount.
  // Keyed by slug so switching artifacts does not compare across dashboards.
  const prevPersistedBySlugRef = useRef<{ slug: string; seq: number } | null>(null);
  useEffect(() => {
    if (!artifact?.saved) return;

    const seq = artifact.lastPersistedPatchSeq;
    const prev = prevPersistedBySlugRef.current;
    const sameSlug = prev?.slug === savedSlug;
    const prevSeq = sameSlug ? prev.seq : null;

    if (!useDbSource || !savedSlug) {
      // Mid-edit: remember this slug's last persisted seq so we detect the advance on sync.
      if (!sameSlug || prevSeq === null) {
        prevPersistedBySlugRef.current = { slug: savedSlug || artifact.slug, seq };
      }
      return;
    }

    prevPersistedBySlugRef.current = { slug: savedSlug, seq };

    const getKey = orpc.dashboard.get.queryOptions({ input: { slug: savedSlug } }).queryKey;

    // Seed + invalidate only when persisted revision advanced for THIS slug (agent/auto-save),
    // not on initial open / artifact switch — get already uses refetchOnMount:'always'.
    if (prevSeq !== null && seq > prevSeq) {
      queryClient.setQueryData(getKey, (old) => {
        if (!old?.dashboard) return old;
        // Store spec uses optional panel.sql; RPC Dashboard requires string sql — normalize via toRpcSpec.
        const rpcSpec = toRpcSpec(artifact.spec);
        return {
          ...old,
          dashboard: {
            ...old.dashboard,
            title: artifact.title,
            description: artifact.description ?? null,
            warehouseId: artifact.warehouseId,
            spec: rpcSpec,
            // Keep prior updatedAt — avoids spurious cache churn; paramSchemaKey is name:type based.
          },
        };
      });

      void queryClient.invalidateQueries({ queryKey: getKey });
      // Scope panel invalidation to this dashboard — avoid refetching unrelated dashboards.
      const queryPath = orpc.dashboard.query.key()[0];
      void queryClient.invalidateQueries({
        queryKey: [queryPath, { input: { dashboardId: artifact.saved.id } }],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lastPersistedPatchSeq is the refresh signal
  }, [artifact?.lastPersistedPatchSeq, artifact?.saved?.id, useDbSource, savedSlug, queryClient, orpc]);

  const displaySpec: DashboardSpec | null = useMemo(() => {
    if (!artifact) return null;
    if (artifact.externallyRemoved) return null;
    if (useDbSource) return dbDashboard?.spec ?? null;
    return artifact.spec;
  }, [artifact, useDbSource, dbDashboard?.spec]);

  const displayTitle = useDbSource && dbDashboard ? dbDashboard.title : (artifact?.title ?? '');
  const displayDashboardId = useDbSource && dbDashboard ? dbDashboard.id : null;
  const previewAnnouncement = useMemo(() => {
    if (!artifact) return '';
    if (artifact.externallyRemoved) return `${artifact.title} is no longer available`;
    if (isPreSaveDraft) return `Draft preview ready for ${displayTitle}`;
    if (isAgentEditing) return `Updating preview for ${displayTitle}`;
    return `Preview updated for ${displayTitle}`;
  }, [artifact, displayTitle, isAgentEditing, isPreSaveDraft]);

  const rpcSpec = useMemo(() => (displaySpec ? toRpcSpec(displaySpec) : null), [displaySpec]);

  const { parameters: resolvedParameters, loadingParamNames } = useDynamicParamOptions(
    displayDashboardId ?? undefined,
    displaySpec?.parameters ?? [],
    orpc,
    undefined,
    !useDbSource && artifact && rpcSpec ? { warehouseId: artifact.warehouseId, spec: rpcSpec } : undefined,
  );

  const defaults = useMemo(() => computeDefaults(resolvedParameters), [resolvedParameters]);

  const paramSchemaKey = useMemo(() => {
    if (!displaySpec) return '';
    const parameters = displaySpec.parameters ?? [];
    const schemaPart = parameters.map((p) => `${p.name}:${p.type}`).join(',');
    if (useDbSource) {
      return `${displayDashboardId ?? ''}:${schemaPart}`;
    }
    return `${artifact?.slug ?? ''}:${artifact?.warehouseId ?? ''}:${schemaPart}`;
  }, [useDbSource, displayDashboardId, artifact?.slug, artifact?.warehouseId, displaySpec]);

  // Reset param controls during render when the schema changes (artifact switch / spec edit) — a
  // post-render effect would let one frame merge the previous dashboard's values into the new query.
  const [paramSchemaKeySeen, setParamSchemaKeySeen] = useState(paramSchemaKey);
  if (paramSchemaKey !== paramSchemaKeySeen) {
    setParamSchemaKeySeen(paramSchemaKey);
    setParamValues({});
  }

  const effectiveParams = useMemo(() => ({ ...defaults, ...paramValues }), [defaults, paramValues]);

  const saveMutation = useMutation(
    orpc.dashboard.save.mutationOptions({
      onMutate: () => ({ savedPatchSeq: artifact?.patchSeq ?? 0 }),
      onSuccess: (result, _variables, context) => {
        const slug = result.dashboard.slug;
        const wasSaved = !!store.artifacts[slug]?.saved;
        const savedPatchSeq = context?.savedPatchSeq;
        store.syncAfterSave(
          slug,
          {
            title: result.dashboard.title,
            slug: result.dashboard.slug,
            warehouseId: result.dashboard.warehouseId,
            description: result.dashboard.description,
            spec: result.dashboard.spec,
          },
          { id: result.dashboard.id, slug: result.dashboard.slug },
          savedPatchSeq,
        );
        void queryClient.invalidateQueries({
          queryKey: orpc.dashboard.list.queryOptions({ input: {} }).queryKey,
        });
        if (useDbSource) {
          void queryClient.invalidateQueries({
            queryKey: orpc.dashboard.get.queryOptions({ input: { slug: result.dashboard.slug } }).queryKey,
          });
        }
        if (chatSessionId) {
          void queryClient.invalidateQueries({
            queryKey: orpc.chat.get.queryOptions({ input: { id: chatSessionId } }).queryKey,
          });
        }
        if (!wasSaved) {
          toast.success('Dashboard saved');
        }
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to save dashboard');
      },
    }),
  );

  const restoreDraftMutation = useMutation(
    orpc.chat.recordDashboardDraftRestored.mutationOptions({
      onSuccess: () => {
        if (chatSessionId) {
          void queryClient.invalidateQueries({
            queryKey: orpc.chat.get.queryOptions({ input: { id: chatSessionId } }).queryKey,
          });
        }
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to save restored draft to chat history');
      },
    }),
  );

  if (!artifact) return null;

  const isDashboardNotFound = useDbSource && dashboardQuery.isError && isOrpcNotFoundError(dashboardQuery.error);
  const isRemoved = artifact.externallyRemoved === true || isDashboardNotFound;
  const sortedPanels = displaySpec ? [...displaySpec.panels].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) : [];
  const panelSource =
    useDbSource && displayDashboardId
      ? { mode: 'saved' as const, dashboardId: displayDashboardId }
      : artifact && rpcSpec
        ? { mode: 'preview' as const, warehouseId: artifact.warehouseId, spec: rpcSpec }
        : null;

  // Gate on isPending (first load) not isFetching — a background dashboard.get refetch (fired on
  // every message send by resolveDashboardDraftForAgent) must not unmount the panel grid and reload.
  const bodyLoading =
    useDbSource && !dashboardQuery.isError && (dashboardQuery.isPending || !displaySpec || !panelSource);
  const bodyError = useDbSource && dashboardQuery.isError && !isOrpcNotFoundError(dashboardQuery.error);
  // First child of both returns, so switching to the not-found state keeps its open time.
  const tracker = (
    <TrackDashboardPreview
      key={artifact.slug}
      settled={isRemoved || (!bodyLoading && (bodyError || (displaySpec !== null && panelSource !== null)))}
      success={!isRemoved && !bodyError && displaySpec !== null}
      panelCount={isRemoved ? 0 : sortedPanels.length}
      dashboardId={displayDashboardId ?? artifact.saved?.id ?? null}
    />
  );

  if (isRemoved) {
    const canRestore = !readOnly && canRestoreDashboardDraft(artifact);
    const notFound = (
      <DashboardPreviewNotFound
        title={artifact.title}
        onClose={store.closePanel}
        onRestoreDraft={
          canRestore
            ? () => {
                store.restoreDraftFromMemory(artifact.slug);
                if (chatSessionId) {
                  restoreDraftMutation.mutate({
                    id: chatSessionId,
                    slug: artifact.slug,
                    title: artifact.title,
                  });
                }
              }
            : undefined
        }
        className={className}
      />
    );
    return (
      <>
        {tracker}
        {notFound}
      </>
    );
  }

  const savedSlugForLink = artifact.saved?.slug ?? artifact.slug;
  const dashboardPath = wp(`/d/${savedSlugForLink}`);
  const isAutoSaving = isAgentEditing;

  const handleSave = (): void => {
    if (!artifact || !rpcSpec) return;
    saveMutation.mutate({
      ...(artifact.saved ? { id: artifact.saved.id } : {}),
      title: artifact.title,
      description: artifact.description ?? null,
      slug: artifact.slug,
      warehouseId: artifact.warehouseId,
      spec: rpcSpec,
      ...(chatSessionId ? { chatSessionId } : {}),
    });
  };

  const headerLoadingSaved = useDbSource && !dashboardQuery.isError && !dbDashboard;
  const showSavedActions = Boolean(artifact.saved) && (Boolean(dbDashboard) || (!useDbSource && !headerLoadingSaved));
  const showSaveDraftAction = !readOnly && !artifact.saved && !headerLoadingSaved && Boolean(rpcSpec);

  const body = (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-1.5">
          <h2 className="truncate text-sm font-semibold text-foreground">{displayTitle}</h2>
          {isPreSaveDraft ? (
            <Badge variant="warning" bordered className={previewStatusBadgeClass}>
              Draft
            </Badge>
          ) : isAutoSaving ? (
            <Badge variant="outline" className={previewStatusBadgeClass}>
              Saving…
            </Badge>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {showSavedActions ? (
            <>
              <Button variant="secondary" asChild className="select-none">
                <Link to={dashboardPath}>Go to dashboard</Link>
              </Button>
              <CopyButton
                text={`${window.location.origin}${dashboardPath}`}
                copyLabel="Dashboard link"
                copiedFeedback
                size="md"
              />
            </>
          ) : showSaveDraftAction ? (
            <Button
              variant="primary"
              label={saveMutation.isPending ? 'Saving…' : 'Save dashboard'}
              loading={saveMutation.isPending}
              disabled={saveMutation.isPending}
              onClick={handleSave}
            />
          ) : null}
          <Button variant="tertiary" aria-label="Close preview" onClick={store.closePanel} Icon={<X />} />
        </div>
      </div>

      <div className="sr-only" aria-live="polite">
        {previewAnnouncement}
      </div>

      <div className="flex-1 overflow-y-auto">
        {bodyLoading ? (
          <div
            className="flex flex-col items-center justify-center gap-3 p-8"
            role="status"
            aria-label="Loading dashboard preview"
          >
            <LoaderCircle className="size-6 animate-spin text-muted-foreground" aria-hidden />
            <Skeleton className="h-4 w-40" />
          </div>
        ) : bodyError ? (
          <Empty className="border-0">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CircleAlert aria-hidden />
              </EmptyMedia>
              <EmptyTitle>Could not load dashboard</EmptyTitle>
              <EmptyDescription>Try closing and reopening the preview.</EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : displaySpec && panelSource ? (
          <>
            {/* ACT2-1286: the panels below say only "Can't run without a warehouse account"; the banner
                says why and offers Repair. Flag off: the off view, so nothing renders. */}
            {useDbSource && displayDashboardId && dashboardQuery.data?.accountRepair && (
              <div className="px-4 pt-4 empty:hidden">
                <AccountRepairBanner
                  itemRef={{ itemType: 'dashboard', id: displayDashboardId }}
                  itemNoun="dashboard"
                  view={dashboardQuery.data.accountRepair}
                  invalidateKeys={[orpc.dashboard.key()]}
                />
              </div>
            )}
            <DashboardParamBar
              parameters={resolvedParameters}
              values={effectiveParams}
              onChange={setParamValues}
              loadingParamNames={loadingParamNames}
            />
            <div className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2">
              {sortedPanels.map((panel) => (
                <div
                  key={panel.id}
                  className={cn('min-w-0', panel.width === 'full' ? 'lg:col-span-2' : 'lg:col-span-1')}
                >
                  <PanelWithData
                    source={panelSource}
                    panel={panel}
                    params={effectiveParams}
                    paramsLoading={loadingParamNames.size > 0}
                    onFixError={
                      onAskAssistant && !readOnly
                        ? (panel, error) => onAskAssistant(`Fix the SQL error in panel "${panel.title}": ${error}`)
                        : undefined
                    }
                  />
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {tracker}
      {body}
    </>
  );
}

interface ArtifactPanelProps {
  store: ChatDashboardArtifactsStore;
  chatSessionId?: string;
  onAskAssistant?: (message: string) => void;
  /** Read-only chat — forwarded to `DashboardPreview`; see `DashboardPreviewProps`. */
  readOnly: boolean;
  /** Keep mounted during exit animation (slide-out). */
  visible?: boolean;
}

export function ArtifactPanel({
  store,
  chatSessionId,
  onAskAssistant,
  readOnly,
  visible,
}: ArtifactPanelProps): React.ReactElement | null {
  const isVisible = visible ?? store.panelOpen;
  if (!store.activeArtifact || !isVisible) return null;

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      <DashboardPreview
        store={store}
        chatSessionId={chatSessionId}
        onAskAssistant={onAskAssistant}
        readOnly={readOnly}
        className="flex-1"
      />
    </div>
  );
}

interface DashboardPreviewMobileSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  store: ChatDashboardArtifactsStore;
  chatSessionId?: string;
  onAskAssistant?: (message: string) => void;
  /** Read-only chat — forwarded to `DashboardPreview`; see `DashboardPreviewProps`. */
  readOnly: boolean;
}

/** Mobile: full-height sheet so chat stays visible underneath (no back-to-chat bar). */
export function DashboardPreviewMobileSheet({
  open,
  onOpenChange,
  store,
  chatSessionId,
  onAskAssistant,
  readOnly,
}: DashboardPreviewMobileSheetProps): React.ReactElement | null {
  if (!store.activeArtifact) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex w-full max-w-full flex-col gap-0 p-0 sm:max-w-full md:hidden"
      >
        <SheetTitle className="sr-only">Dashboard preview</SheetTitle>
        <SheetDescription className="sr-only">
          Live preview of the dashboard being created or edited in this chat.
        </SheetDescription>
        <DashboardPreview
          store={store}
          chatSessionId={chatSessionId}
          onAskAssistant={onAskAssistant}
          readOnly={readOnly}
          className="flex-1"
        />
      </SheetContent>
    </Sheet>
  );
}
